import React, { memo, useMemo } from "react";
import { HappycoWorkOrderFragment } from "gql/graphql";
import colors from "app/colors";
import { Text, HStack, Circle } from "@chakra-ui/react";
import { PauseIcon, ThickCheckIcon } from "modules/icons";

interface IProps {
  happycoWorkOrder: HappycoWorkOrderFragment;
  includeLabel?: boolean;
  includeIcon?: boolean;
  height?: string;
  width?: string;
  color?: string;
  fontSize?: string;
}

const HappycoWorkOrderStatus = ({
  happycoWorkOrder,
  includeLabel = true,
  includeIcon = true,
  height = "24px",
  width = "80px",
  color = colors.gray50,
  fontSize = "12px",
}: IProps) => {
  const bgColor = useMemo(() => {
    switch (happycoWorkOrder.status) {
      case "COMPLETED":
        return colors.green10;
      case "ON_HOLD":
        return colors.yellow30;
      default:
        return colors.purple30;
    }
  }, [happycoWorkOrder.status]);

  const icon = useMemo(() => {
    if (!includeIcon) {
      return null;
    }
    switch (happycoWorkOrder.status) {
      case "COMPLETED":
        return <ThickCheckIcon boxSize={4} fill={colors.greenPrimary} />;
      case "ON_HOLD":
        return <PauseIcon boxSize={3} fill={colors.yellowDark} />;
      default:
        return <Circle size="10px" bg={colors.purple20} />;
    }
  }, [happycoWorkOrder.status, includeIcon]);

  const text = useMemo(() => {
    switch (happycoWorkOrder.status) {
      case "COMPLETED":
        return "Complete";
      case "ON_HOLD":
        return "On Hold";
      default:
        return "Open";
    }
  }, [happycoWorkOrder.status]);

  if (!happycoWorkOrder.status) {
    return null;
  }

  return (
    <HStack spacing={1}>
      {includeLabel && (
        <Text fontSize={fontSize} fontWeight={500} color={colors.gray50}>
          Status
        </Text>
      )}
      <HStack
        h={height}
        w={width}
        bg={bgColor}
        borderRadius={4}
        px={1}
        py="2px"
        justifyContent="center"
        spacing="2px"
      >
        {icon}
        <Text fontSize={fontSize} fontWeight={700} color={color}>
          {text}
        </Text>
      </HStack>
    </HStack>
  );
};

export default memo(HappycoWorkOrderStatus);
